import type { Segment } from "@ui/components/demos/STT/types";

import { useTranslations } from "next-intl";

import { getImageProps } from "@/app/_utilities/payload";
import { sizes } from "@ui/lib/sizes";
import type { Media } from "@mw/shared/payload-types";

import type { STTHeroDisplayMode } from "./useSTTHeroPlayback";
import {
  formatSttHeroPreviewTime,
  getSttHeroPreviewDurationSeconds,
  getSttHeroPreviewFileType,
  shouldShowSttHeroVideoUploadPreview,
} from "./sttHeroVideoPreview";

export type STTFloatingCardHeaderModel =
  | {
      kind: "legacy";
      title: string;
      showDottedDivider: true;
    }
  | {
      kind: "videoPreviewUpload";
      title: string;
      fileType: string;
      durationLabel: string;
      uploadedThumbnailUrl: string | null;
      showDottedDivider: false;
    }
  | {
      kind: "videoPreviewSample";
      title: string;
      fileType: string;
      durationLabel: string;
      thumbnailProps: NonNullable<ReturnType<typeof getImageProps>>;
      thumbnailSizes: string;
      showDottedDivider: false;
    };

function createLegacyHeaderModel(title: string) {
  return { kind: "legacy", title, showDottedDivider: true } as const;
}

function resolveHeaderModel({
  displayMode,
  sampleThumbnail,
  uploadedMediaKind,
  sampleName,
  segments,
  uploadFileName,
  uploadedThumbnailUrl,
  getFilenameText,
  getDurationLabel,
}: {
  displayMode: STTHeroDisplayMode;
  sampleThumbnail: Media | null;
  uploadedMediaKind: "audio" | "video" | null;
  sampleName: string;
  segments: Segment[];
  uploadFileName: string | null;
  uploadedThumbnailUrl: string | null;
  getFilenameText: (name: string) => string;
  getDurationLabel: (seconds: number) => string;
}) {
  const legacyTitle = uploadFileName ?? getFilenameText(sampleName);
  if (displayMode !== "videoPreview") {
    return createLegacyHeaderModel(legacyTitle);
  }

  const previewDurationLabel = getDurationLabel(getSttHeroPreviewDurationSeconds(segments));

  if (shouldShowSttHeroVideoUploadPreview(displayMode, uploadFileName, uploadedMediaKind)) {
    const uploadPreviewTitle = (uploadFileName ?? sampleName).replace(/\.[^/.]+$/, "");
    return {
      kind: "videoPreviewUpload" as const,
      title: uploadPreviewTitle,
      fileType: getSttHeroPreviewFileType(uploadFileName, uploadedMediaKind),
      durationLabel: previewDurationLabel,
      uploadedThumbnailUrl,
      showDottedDivider: false as const,
    };
  }

  if (uploadFileName && uploadedMediaKind === "audio") {
    return createLegacyHeaderModel(legacyTitle);
  }

  if (!uploadFileName && sampleThumbnail?.url) {
    const thumbProps = getImageProps(sampleThumbnail, { placeholder: true, fill: true });
    if (!thumbProps) {
      return createLegacyHeaderModel(legacyTitle);
    }
    return {
      kind: "videoPreviewSample" as const,
      title: sampleName,
      // Samples are audio-only files but are presented as video content (audio + thumbnail),
      // so we fake the extension to match the visual treatment rather than exposing the real audio format.
      fileType: "mp4",
      durationLabel: previewDurationLabel,
      thumbnailProps: thumbProps,
      thumbnailSizes: sizes("327px"),
      showDottedDivider: false as const,
    };
  }

  return createLegacyHeaderModel(legacyTitle);
}

export function useSTTFloatingCardHeader({
  displayMode,
  sampleThumbnail,
  uploadedMediaKind,
  sampleName,
  segments,
  uploadFileName,
  uploadedThumbnailUrl,
}: {
  displayMode: STTHeroDisplayMode;
  sampleThumbnail: Media | null;
  uploadedMediaKind: "audio" | "video" | null;
  sampleName: string;
  segments: Segment[];
  uploadFileName: string | null;
  uploadedThumbnailUrl: string | null;
}) {
  const t = useTranslations("ClientKeys.STTHero");

  return resolveHeaderModel({
    displayMode,
    sampleThumbnail,
    uploadedMediaKind,
    sampleName,
    segments,
    uploadFileName,
    uploadedThumbnailUrl,
    getFilenameText: name => t("filename", { name }),
    getDurationLabel: seconds => t("video_preview_duration", { time: formatSttHeroPreviewTime(seconds) }),
  });
}
